// Admin blog hooks — list + create/update/delete (step-up) + cover upload.
// Mirrors the /admin/* pattern in useAdmin.ts: writes take a stepUpToken
// obtained via the StepUpContext and sent as the X-StepUp header.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { getAccessToken } from "@/lib/tokens";

export interface AdminBlogPost {
  id: string;
  slug: string;
  locale: string;
  title: string;
  excerpt?: string | null;
  cover_image_url?: string | null;
  body_html: string;
  status: "draft" | "published";
  published_at?: string | null;
  author_user_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface BlogPostInput {
  title: string;
  slug?: string;
  locale: "pt" | "en" | "es";
  excerpt?: string;
  cover_image_url?: string;
  body_html: string;
  status: "draft" | "published";
}

function stepUpHeaders(token: string): Record<string, string> {
  return { "X-StepUp": token };
}

export function useAdminBlogList() {
  return useQuery({
    queryKey: ["admin", "blog"],
    queryFn: () => api<AdminBlogPost[]>("/admin/blog"),
  });
}

export function useCreateBlogPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ input, stepUpToken }: { input: BlogPostInput; stepUpToken: string }) =>
      api<AdminBlogPost>("/admin/blog", {
        method: "POST",
        body: input,
        headers: stepUpHeaders(stepUpToken),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "blog"] }),
  });
}

export function useUpdateBlogPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
      stepUpToken,
    }: {
      id: string;
      input: Partial<BlogPostInput>;
      stepUpToken: string;
    }) =>
      api<AdminBlogPost>(`/admin/blog/${id}`, {
        method: "PATCH",
        body: input,
        headers: stepUpHeaders(stepUpToken),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "blog"] }),
  });
}

export function useDeleteBlogPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stepUpToken }: { id: string; stepUpToken: string }) =>
      api<void>(`/admin/blog/${id}`, {
        method: "DELETE",
        headers: stepUpHeaders(stepUpToken),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "blog"] }),
  });
}

// Cover-image upload uses multipart/form-data, so it bypasses the JSON
// `api()` helper and posts a FormData directly with the auth + step-up
// headers. Returns the stored first-party URL (/media/blog/...).
export async function uploadCoverImage(
  file: File,
  stepUpToken: string,
): Promise<string> {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch("/api/v1/admin/blog/upload", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getAccessToken() ?? ""}`,
      "X-StepUp": stepUpToken,
    },
    body: fd,
  });
  if (!res.ok) {
    const detail = await res
      .json()
      .then((d) => d.detail)
      .catch(() => null);
    throw new Error(typeof detail === "string" ? detail : "Upload failed");
  }
  return (await res.json()).url as string;
}
